/**
 * VyuctovaniSheet — uzávěrka pokladny rozvozce.
 *
 * S `inline` se vykreslí **jako pracovní plocha cockpitu**, pod stálým pruhem
 * s osou fází, bez vlastního povrchu a bez hlavičky. Plné překrytí sheetem
 * odpadlo: schovalo osu fází i dlaždice auta a pokladny, takže z obrazovky
 * zmizelo, kde ve směně člověk je. Bez `inline` zůstává původní fullscreen
 * sheet (používá se tam, kde cockpit není).
 *
 * Flow:
 *   1. Načti `/vyuctovani-preview` (sumy z objednávek — A/B/C/F/G/Body + limity)
 *   2. `UzaverkaStranka` — systém → terminál → hotovost → výsledek, a doložení
 *      manka se rozbalí pod výsledkem, když ho je potřeba
 *   3. Po odeslání → `clearDraft()` + `onSubmitted()` + `onClose()`
 *
 * Dvoukrokové flow (Step1 „Uzávěrka pokladny" → Step2 „Doložit manko") zrušeno:
 * druhý krok byl reakcí na to, co člověk právě zadal, ne samostatná stanice,
 * a rozdělení navíc odsouvalo výsledek pod přehyb.
 *
 * Stav je v `useUzaverkaDraft` (per-smenaId localStorage), takže rozpracovaná
 * uzávěrka přežije refresh/zavření prohlížeče. Backend výpočty zůstávají
 * autoritativní — klient zde dělá jen klient-side preview pro orientaci.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { CheckCircle2 } from 'lucide-react';
import BottomSheet from './BottomSheet';
import { SheetButton, SheetAlert } from './SheetUI';
import { SHEET_TOKENS, getTextColors } from './sheetStyle';
import { useUzaverkaDraft } from './useUzaverkaDraft';
import UzaverkaStranka from './UzaverkaStranka';
import ClockInManualModal from './ClockInManualModal';

const fmtKc = (n) => (n == null || isNaN(n)
    ? '—'
    : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');

const fmtKcDelta = (n) => {
    if (n == null || isNaN(n) || n === 0) return '0 Kč';
    const sign = n > 0 ? '+' : '−';
    return `${sign}${Math.round(Math.abs(n)).toLocaleString('cs-CZ')} Kč`;
};

export default function VyuctovaniSheet({
    open, onClose,
    /* Pracovní plocha cockpitu místo překrytí sheetem. */
    inline = false,
    onSubmitted,
    smenaId,
    pokladnaSmena,
    /* Navrhovaný čas ukončení směny (HH:MM) — endTimeOverride z parentu nebo
       smena.cas_do. Zobrazí se v popisku „Ukončit ve X" v Kroku 1/2. */
    proposedClockOut = null,
    /* Bubble up vybraný čas z „V jiný čas" picker do parent state. Po onSubmitted
       parent přepne na UzaverSmenyFinal, který přečte endTimeOverride. */
    onEndTimeChange = null,
    isDark,
    isMobile,
}) {
    const { draft, setDraft, clearDraft } = useUzaverkaDraft(smenaId);

    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    // Vědomé odeslání nad neověřenými daty (spadlý scraper) — viz ZamkyUzaverky.
    const [prekrocitData, setPrekrocitData] = useState(false);

    // Lokální HH:MM pro tlačítka „Ukončit ve X" v Kroku 1/2. Init z prop, ale
    // může se přepsat „V jiný čas" pickerem — změna se bublá do parentu, aby
    // UzaverSmenyFinal (otevřený po onSubmitted) četl správný čas.
    const [endTime, setEndTime] = useState(proposedClockOut || '');
    const [timePickerOpen, setTimePickerOpen] = useState(false);

    // Reset stepu při zavření (draft zůstává v localStorage)
    useEffect(() => {
        if (!open) {
            setSubmitError(null);
        }
    }, [open]);

    // Sync endTime když rodič změní proposedClockOut (např. otevření sheetu po
    // změně endTimeOverride odjinud).
    useEffect(() => {
        if (proposedClockOut) setEndTime(proposedClockOut);
    }, [proposedClockOut]);

    // Preview fetch — A/B/C/F/G/Body + limity + zámky uzávěrky z backendu.
    // Je to akce, ne jednorázový efekt: zámky se dají zkontrolovat znovu
    // a po odmítnutém odeslání (mezitím doskočila objednávka) se čísla musí
    // obnovit, aniž by se zavírala obrazovka.
    const nactiPreview = useCallback(async () => {
        if (!smenaId) return null;
        setLoading(true);
        setFetchError(null);
        try {
            const r = await fetch(`/api/me/smena/${smenaId}/vyuctovani-preview`);
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
            setPreview(data);
            return data;
        } catch (e) {
            setFetchError(e.message || 'Načtení vyúčtování selhalo.');
            return null;
        } finally {
            setLoading(false);
        }
    }, [smenaId]);

    useEffect(() => {
        if (!open || !smenaId) return;
        setPrekrocitData(false);
        nactiPreview();
    }, [open, smenaId, nactiPreview]);

    // Spočti E:
    //  - manual mode (draft.manualE !== null) → použít zadanou částku ručně
    //  - denom mode → sum(denom × nominal). Backend zůstává autoritativní.
    const totalE = useMemo(() => {
        if (draft.manualE !== null && draft.manualE !== undefined) {
            return Number(draft.manualE) || 0;
        }
        let s = 0;
        for (const [nominal, count] of Object.entries(draft.denom || {})) {
            s += Number(nominal) * Number(count || 0);
        }
        return s;
    }, [draft.denom, draft.manualE]);

    // Klient-side preview přepočtu (identický vzorec jako VyuctovaniService::vyhodnot).
    // Znaménkové dýško/manko per typ (+ = dýško, − = manko); součet = net. Model
    // odolný vůči záměně hotovost↔karta — pracuje se součtem F+G, ne s rozdělením.
    const computed = useMemo(() => {
        if (!preview) return null;
        const G = preview.G_kartova_trzba || 0;
        const F = preview.F_hotovostni_trzba || 0;
        const B = preview.B_online || 0;
        const C = preview.C_dysko_online || 0;
        const Z = preview.pocatecni_castka || 0;
        // Nákupy zaplacené hotově z tašky — místo peněz odevzdá účtenku.
        const H = preview.H_nakupy_hotove || 0;
        // Dokud není terminál opsaný, **nedosazuje se nic** — ani v živém
        // náhledu. S dosazeným G vycházel kartový rozdíl vždycky v nule
        // a stránka tvrdila „sedí" o čísle, které nikdo nezadal.
        const dParsed = parseFloat(draft.D);
        const maD = Number.isFinite(dParsed) && dParsed >= 0;
        // Spodní hranice: základ bez toho, co odešlo z tašky na účtenky.
        const minE = Math.max(0, (preview.pocatecni_castka || 0) - H);
        const maE = totalE > 0 && totalE >= minE;
        const D = maD ? dParsed : 0;
        const E = totalE;

        const dyskoOnline   = +Number(C).toFixed(2);          // online spropitné, ≥ 0
        const dyskoKarta    = +(D - G).toFixed(2);            // terminál vs systém
        const dyskoHotovost = +((E - Z + H) - F).toFixed(2); // hotovost nad základ + účtenky z tašky vs hotovostní tržba
        const net           = +(dyskoOnline + dyskoKarta + dyskoHotovost).toFixed(2);
        // Hotovost k odevzdání před zálohou = celá hotovost nad základ − net ≡ (F+G)−C−D.
        const trzbaKOdevzdani = +((E - Z) - net).toFixed(2);

        const eskLimit = preview.manko_limit_eskalace_kc || 100;
        const autoLimit = preview.manko_limit_auto_kc || 20;
        // Foto + komentář se vyžaduje, AŽ když net klesne pod −eskLimit (dýška
        // „pohltí" manko). Manko = net < 0.
        // Doložení má smysl až nad hotovým výpočtem — s prázdnými poli by
        // formulář rovnou křičel manko, které si sám vyrobil.
        const vyzadujeOpatreni = maD && maE && net < -eskLimit;
        return {
            F, G, B, C,
            // `maD`/`maE` říkají stránce, které rozdíly už dávají smysl —
            // bez nich by nula vypadala jako „sedí" místo „nezadáno".
            maD, maE, hotovo: maD && maE,
            dyskoHotovost, dyskoKarta, dyskoOnline,
            net, trzbaKOdevzdani,
            vyzaduje_komentar: vyzadujeOpatreni,
            vyzaduje_foto: vyzadujeOpatreni,
            eskLimit, autoLimit,
        };
    }, [preview, draft.D, totalE]);

    const odeslano = preview?.stav && preview.stav !== 'otevreno';

    // „V jiný čas" — picker po confirmu rovnou odešle uzávěrku se zvoleným
    // časem. endTime se propíše do parent (endTimeOverride), aby UzaverSmenyFinal
    // (otevřený po onSubmitted) četl správný čas.
    const handlePickTime = (hhmm) => {
        if (!hhmm) { setTimePickerOpen(false); return; }
        setEndTime(hhmm);
        onEndTimeChange?.(hhmm);
        setTimePickerOpen(false);
        // Zvolený čas jde do odeslání **přímo**. Dřív se odesílalo v dalším
        // tiku přes setTimeout — jenže ten volal handleSubmit z tohohle
        // renderu, kde `endTime` pořád drží starou hodnotu. Server tak dostal
        // původní konec (19:40 místo zvolených 20:00) a mzda i částka
        // k předání vyšly z něj, na obrazovce kolegy i v kartě manažera.
        handleSubmit(hhmm);
    };

    // `casKonce` — konec, se kterým se uzávěrka odesílá. Bez něj ten
    // z tlačítka (`endTime`); „Jiný čas" ho předá napřímo, viz výš.
    const handleSubmit = async (casKonce = endTime) => {
        if (submitting) return;
        setSubmitError(null);
        // D = částka opsaná z terminálu. **Nic se nedosazuje.** Dřív se za
        // prázdné pole doplnila systémová kartová tržba (G) — tedy přesně to
        // číslo, které má terminál ověřovat. Kdo formulář proklikl, měl
        // kartovou stranu vždycky v nule, protože se porovnala sama se sebou.
        const D = parseFloat(draft.D);
        if (!Number.isFinite(D) || D < 0) { setSubmitError('Opiš částku z uzávěrky terminálu.'); return; }
        // Hotovost musí aspoň dosáhnout na ranní základ — nulová peněženka
        // dřív prošla i u směny s hotovostní tržbou. Co odešlo z tašky na
        // účtenky, v ní chybět smí: základ pak doplní manažer při vyrovnání.
        const minE = Math.max(0, (preview?.pocatecni_castka || 0) - (preview?.H_nakupy_hotove || 0));
        if (!(totalE > 0) || totalE < minE) {
            setSubmitError(`Napočítej hotovost v peněžence — musí v ní být aspoň ${fmtKc(minE)}.`);
            return;
        }
        if (computed?.vyzaduje_komentar && !draft.komentar?.trim()) {
            setSubmitError('Manko přesahuje tvoje dýška — vyplň komentář.');
            return;
        }
        if (computed?.vyzaduje_foto && !draft.fotoPath) {
            setSubmitError('Manko přesahuje tvoje dýška — nahraj fotku hotovosti a lístku z terminálu.');
            return;
        }
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
        setSubmitting(true);
        try {
            const r = await fetch(`/api/me/smena/${smenaId}/vyuctovani-odeslat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf, Accept: 'application/json' },
                body: JSON.stringify({
                    D, E: totalE,
                    // V manual modu posíláme prázdnou denom mapu — backend pak
                    // přeskočí sum-check (audit per denominace nemá smysl, když
                    // uživatel zadal jen výsledek klávesnicí).
                    hotovost_denominace: draft.manualE !== null && draft.manualE !== undefined
                        ? null
                        : (draft.denom || null),
                    foto_penezenky_path: draft.fotoPath || null,
                    komentar: draft.komentar?.trim() || null,
                    // Čas konce, se kterým se uzávěrka odesílá. Manažer podle
                    // něj uvidí zálohu mzdy, a tedy i kolik si mají předat —
                    // bez něj by každý počítal z jiného okamžiku.
                    navrzeny_clock_out: (typeof casKonce === 'string' && casKonce) || null,
                    // Tržby z obrazovky — server je porovná s aktuálními a při
                    // rozdílu vyúčtování odmítne (409). Jinak by kolega odeslal
                    // manko, které za minutu po doskočení objednávek zmizí.
                    // Jen čísla, která **má na očích**: očekávaný stav fleku
                    // do uzávěrky rozvozce nevstupuje a hláška o jeho změně by
                    // mluvila o hodnotě, kterou nikde nevidí.
                    otisk: preview?.otisk ? {
                        trzba_hotovost_kc: preview.otisk.trzba_hotovost_kc,
                        trzba_karta_kc:    preview.otisk.trzba_karta_kc,
                        dysko_online_kc:   preview.otisk.dysko_online_kc,
                        nakupy_hotove_kc:  preview.otisk.nakupy_hotove_kc,
                    } : null,
                    prekrocit_stara_data: prekrocitData,
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setSubmitError(data.error || data.message || `Odeslání selhalo (HTTP ${r.status}).`);
                // Zámek nebo posunutá čísla — čerstvý náhled hned, ať kolega
                // nekouká na hodnoty, které server právě odmítl.
                if (r.status === 409 || data?.zamky) await nactiPreview();
                return;
            }
            clearDraft();
            onSubmitted?.();
            onClose?.();
        } catch (err) {
            setSubmitError('Odeslání selhalo: ' + (err?.message || 'neznámá chyba'));
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Render ──────────────────────────────────────────────────────────────

    const obsah = (
        <>
            {loading && (
                <CenteredText isDark={isDark}>Načítám…</CenteredText>
            )}

            {fetchError && (
                <div style={{ padding: SHEET_TOKENS.spacing.sheetPadX }}>
                    <SheetAlert kind="error" isDark={isDark}>{fetchError}</SheetAlert>
                </div>
            )}

            {!loading && !fetchError && preview && odeslano && (
                <UzaverkaOdeslanoState onClose={onClose} isDark={isDark} />
            )}

            {!loading && !fetchError && preview && !odeslano && (
                <UzaverkaStranka
                    preview={preview}
                    computed={computed}
                    draft={draft}
                    setDraft={setDraft}
                    endTime={endTime}
                    submitting={submitting}
                    submitError={submitError}
                    onSubmit={() => handleSubmit()}
                    onPickOtherTime={() => setTimePickerOpen(true)}
                    prekrocitData={prekrocitData}
                    onPrekrocitData={setPrekrocitData}
                    onZkontrolovatZnovu={nactiPreview}
                    kontroluji={loading}
                    isDark={isDark}
                    isMobile={isMobile}
                />
            )}

            {timePickerOpen && (
                <ClockInManualModal
                    autoTime={endTime || (preview?.cas_do ? String(preview.cas_do).slice(0, 5) : '')}
                    busy={submitting}
                    title="Vlastní čas konce směny"
                    accent="#10B981"
                    confirmLabelPrefix="Ukončit v"
                    busyLabel="Odesílám…"
                    onClose={() => setTimePickerOpen(false)}
                    onConfirm={handlePickTime}
                    isDark={isDark}
                />
            )}
        </>
    );

    // Inline = plocha modulu: žádný povrch, žádné překrytí. Pozadí nese záře
    // aplikace pod ním (norma: roztažené sklo přes obrazovku ji jen rozmaže
    // a jeho hrana se čte jako předěl).
    if (inline) {
        return (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {obsah}
            </div>
        );
    }

    return (
        <BottomSheet
            open={open} onClose={onClose}
            isDark={isDark} isMobile={isMobile}
            title={null}
            fullscreen={true}
            hideClose={true}
        >
            {obsah}
        </BottomSheet>
    );
}

// ─── Pomocné komponenty ──────────────────────────────────────────────────────

function CenteredText({ children, isDark }) {
    const colors = getTextColors(isDark);
    return (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: colors.textMuted, fontSize: 13 }}>
            {children}
        </div>
    );
}

function UzaverkaOdeslanoState({ onClose, isDark }) {
    const colors = getTextColors(isDark);
    return (
        <>
            <div style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
                padding: SHEET_TOKENS.spacing.sheetPadX,
            }}>
                <CheckCircle2 size={48} style={{ color: colors.success }} />
                <span style={{ fontSize: 18, fontWeight: 800, color: colors.textPrimary, textAlign: 'center' }}>
                    Vyúčtování odesláno
                </span>
                <span style={{ fontSize: 13, color: colors.textMuted, textAlign: 'center', maxWidth: 280 }}>
                    Pokladna je uzavřena, čeká na schválení manažerem. Můžeš pokračovat v ukončování směny.
                </span>
            </div>
            <div style={{
                flexShrink: 0,
                padding: `12px ${SHEET_TOKENS.spacing.sheetPadX + 2}px 14px`,
                borderTop: `1px solid ${colors.divider}`,
            }}>
                <SheetButton isDark={isDark} variant="primary" accent="#0CC0DF" accent2="#0CA5BA" onClick={onClose}>
                    Zavřít
                </SheetButton>
            </div>
        </>
    );
}

